// Navigation uses live screen state; it never submits a form or resets a search.
export function navigationCommand(message, context = {}) {
  const text = message.toLowerCase().trim().replace(/[.!?]+$/, '').replace(/^(?:(?:please|can you|could you|would you) )+/, '').replace(/ please$/, '');
  const request = text.replace(/^(?:okay[, ]*|yeah[, ]*)/, '').replace(/^(?:let's|lets|i want to|i would like to|i'd like to) /, '');
  const saveCommand = saveFormCommand(message, context);
  if (saveCommand) return saveCommand;
  if (context.save_open && /^(?:cancel(?: (?:the )?save)?|skip(?: for now)?|not now|no thanks|never mind|nevermind)$/.test(text)) return {command:'cancel_save'};
  if (context.showing_open && !context.save_open && /^(?:skip(?: (?:the )?showing)?(?: for now)?|not now|no thanks|keep browsing|cancel(?: (?:the )?showing)?)$/.test(text)) return {command:'cancel_showing'};
  if (context.save_open) {
    // Other requests about an open save form are handled by the assistant.
  } else if (!context.showing_open && !context.email_open && !context.seller_review_open) {
    if (/^(?:show|open)(?: me)?(?: all)?(?: of)?(?: my| our| the)? (?:favorites|saved (?:homes|properties))$/.test(text)) return {command:'show_favorites'};
    if (context.view === 'results' && !context.preferences_open && !context.comparison_open) {
      if (/^(?:scroll|move)(?: the (?:properties|tiles|results))? (?:up|down)$/.test(text)) return {command:text.endsWith('up')?'scroll_results_up':'scroll_results_down'};
      const tile = request.match(/^(show|open|see|view|scroll to)(?: me)? (?:the )?(?:(?:property|home|house|tile)(?: number)?|number) (one|two|three|four|five|six|[1-6])$/);
      if (tile) return {command:tile[1]==='scroll to'?'show_tile':'open_property',number:Number(tile[2]) || ['one','two','three','four','five','six'].indexOf(tile[2])+1};
      if (/^(?:next|next page|next batch|next (?:6|six)(?: homes| properties)?|show(?: me)? (?:the )?next(?: page| batch| (?:6|six)(?: homes| properties)?)?)$/.test(text)) return {command:'next_page'};
      if (/^(?:previous|previous page|previous batch|previous (?:6|six)(?: homes| properties)?)$/.test(text)) return {command:'previous_page'};
    }
  }
  // Explicit photo exits target the gallery, even if another panel is also open.
  if (/^(?:(?:get|take me|go) out of|(?:exit|close)(?: out of)?) (?:the |this |that |these |those )?(?:images?|photos?|pictures?|gallery)(?: view)?(?: and (?:go |take me )?back to (?:the )?property details(?: screen)?)?$/.test(request)) return {command:'close_photos'};
  if (/^(?:(?:go |take me )?back|exit|close|(?:exit|close)(?: out of)? (?:this|that)|(?:exit|close)(?: out of)?(?: the| this| that)? (?:comparison|compare screen|form|save form|showing form))$/.test(text)) return { command: 'go_back' };
  if (/^(?:(?:go |take me )?back|return)(?: to)? (?:the |my )?(?:search(?: results)?|results|property list)(?: for more (?:properties|homes))?$/.test(text)) return { command: 'return_to_results' };
  if (/^(?:(?:(?:go |take me )?back|return)(?: to)?|show(?: me)?|open) (?:the |this )?(?:property(?: details)?|home|details)(?: screen| page| view)?$/.test(request)) return { command: 'return_to_property' };
  if (!context.save_open && !context.showing_open && !context.email_open && !context.seller_review_open) {
    const save = request.match(/^(?:save|favorite|like)(?: (?:this|that|the))?(?: (?:home|property|properties))?(?: it)?(?: (?:to|in)(?: my| our)? favorites)?$/);
    if (save && context.selected_property) return {command:'save_property'};
    if (/^(?:schedule|request|book)(?: me)?(?: a| the)? (?:showing|tour)(?: (?:of|for) (?:this|that)(?: home| property)?)?$/.test(request) && context.selected_property) return {command:'open_showing'};
  }
  if (/^zoom (?:in|closer)(?: on (?:the )?map)?$/.test(text)) return { command: 'zoom_map_in' };
  if (/^zoom (?:out|further out)(?: on (?:the )?map)?$/.test(text)) return { command: 'zoom_map_out' };
  if (/^(?:show|open)(?: me)? (?:the |this )?map$/.test(text)) return { command: 'open_map' };
  if (/^close (?:the )?map$/.test(text)) return { command: 'close_map' };
  return null;
}
export function backCommand(context) {
  if (context.save_open) return 'cancel_save';
  if (context.map_open && ['property', 'results'].includes(context.map_kind)) return 'close_map';
  if (context.email_open) return 'cancel_email';
  if (context.showing_open) return 'cancel_showing';
  if (context.nearby_open) return 'close_nearby';
  if (context.staging_open) return 'close_staging';
  if (context.gallery_open) return 'close_photos';
  if (context.comparison_open) return 'close_compare';
  if (context.preferences_open) return 'close_preferences';
  if (context.selected_property) return 'close_property';
  return null;
}
export function mapCoordinates(property) {
  const rawLat = property?.latitude ?? property?.location?.latitude;
  const rawLon = property?.longitude ?? property?.location?.longitude;
  if ([rawLat, rawLon].some(v => v == null || String(v).trim() === '')) return null;
  const lat = Number(rawLat), lon = Number(rawLon);
  return Number.isFinite(lat) && Number.isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180 ? { lat, lon } : null;
}
export function mapPagePoints(page) {
  return page.slice(0, 6).flatMap((property, index) => {
    const coords = mapCoordinates(property);
    return coords ? [{ ...coords, number: index + 1, listingKey: property.listing_key }] : [];
  });
}
export function zoomMap(map, direction) {
  if (!map) return 'The map is still loading. Please try zooming again in a moment.';
  const current = map.getZoom();
  const minimum = Number.isFinite(map.getMinZoom()) ? map.getMinZoom() : 1;
  const maximum = Number.isFinite(map.getMaxZoom()) ? map.getMaxZoom() : 19;
  const next = Math.min(maximum, Math.max(minimum, current + direction));
  if (next === current) return direction > 0 ? 'The map is already zoomed all the way in.' : 'The map is already zoomed all the way out.';
  map.setZoom(next, { animate: false });
  return direction > 0 ? 'Zoomed in on the map.' : 'Zoomed out on the map.';
}

// Scroll the active six-card panel, never the document or the search cursor.
export function scrollPropertyResults({panel, rail, mobile, command, number}) {
  if (!panel || !rail) return 'Search for homes or open your favorites first.';
  const cards = [...rail.querySelectorAll('[data-property-index]')];
  if (!cards.length) return 'There are no homes on this page to scroll through.';
  const surface = mobile ? rail : panel;
  const axis = mobile ? 'left' : 'top';
  const current = mobile ? surface.scrollLeft : surface.scrollTop;
  const size = mobile ? surface.clientWidth : surface.clientHeight;
  const maximum = Math.max(0, (mobile ? surface.scrollWidth : surface.scrollHeight) - size);
  let target;
  if (command === 'show_tile') {
    const card = cards.find(el => Number(el.dataset.propertyIndex) === number - 1);
    if (!card) return 'Choose a property number on this page, from one through six.';
    target = current + card.getBoundingClientRect()[axis] - surface.getBoundingClientRect()[axis] - (mobile ? 0 : 16);
  } else target = current + (command === 'scroll_results_up' ? -1 : 1) * size * 0.8;
  target = Math.min(maximum, Math.max(0, target));
  const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  surface.scrollTo({[axis]:target,behavior});
  if (command === 'show_tile') return `Property ${number} is in view. Say open property ${number} to see its details.`;
  if (Math.abs(target-current) < 2) return command === 'scroll_results_up' ? 'You are at the start of this page. Say previous page for the previous six homes.' : 'You are at the end of this page. Say next page for the next six homes.';
  return 'Moved through the homes on this page.';
}


export function saveFormCommand(message, context = {}) {
  const text = message.toLowerCase().replace(/[.!?,;]+/g, ' ').trim().replace(/\s+/g, ' ')
    .replace(/^(?:okay|ok)[ ]+/, '').replace(/ please$/, '');
  // Reports and questions check the actual result; they must never submit again.
  if ((context.save_open || context.save_result) && /^(?:i (?:already )?(?:submitted(?: it| the form)?|saved (?:it|the property))(?: it (?:looks|looked) like it saved)?|did (?:it|the property) save|(?:is|was) (?:it|the property) saved|(?:check|what is) (?:the )?save status)$/.test(text)) return {command:'get_save_status'};
  if (!context.save_open) return null;
  const request = text.replace(/^i (?:made a correction|corrected (?:it|the form)) (?:and )?/, '')
    .replace(/^(?:can|could|would) you /, '').replace(/^please /, '')
    .replace(/^(?:you can )?go ahead and /, '')
    .replace(/^it looks like the (?:save )?form is (?:filled out|complete) /, '')
    .replace(/^you just have to /, '');
  if (/^(?:confirm save|confirm and save|save (?:it|this|(?:the |this )?property)(?: for me)?|submit (?:it|(?:the |this )?(?:save )?form)(?: for me)?|yes(?: (?:please|save it|confirm save))?)$/.test(request)) return {command:'confirm_save'};
  return null;
}
